import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ConversationPatch, serializePatch} from '../../../models/api/conversationPatchModel';
import {ApiJob} from '../../../models/api/jobModel';
import {postApiAsync} from '../../../services/http/http';
import {HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {ThunkAsyncAction} from '../../appThunk';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../panels/inboxes/parameters/inboxesParameters';
import {
  buildFullSearchQuery,
  buildParametersFetchQueryContext,
} from '../../panels/inboxes/parameters/inboxesParametersFetchHelpers';
import {computeIsAllExceptSelection} from '../../panels/inboxes/parameters/inboxesParametersHelpers';
import {getCompanyUrl} from '../company/companySelectors';
import {jobUpdatedAction} from '../jobs/jobsActions';

/*
 * Bulk Actions.
 */

/** Updates conversations in bulk (used for the select all feature). */
export function updateConversationsBulkAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  patch: ConversationPatch,
): ThunkAsyncAction {
  if (inboxesParameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
    throw new Error('Bulk actions not supported for Drafts or Sent.');
  }

  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);

    try {
      // Perform the request.
      const url = `${companyUrl}/select_all`;
      const body = {
        conversations: serializeConversationSelection(inboxesParameters, cellsContext),
        patch: serializePatch(patch),
      };

      const job = await postApiAsync(ApiJob, url, body);
      dispatch(jobUpdatedAction(job));
    } catch (error) {
      const isNetworkError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
      if (isNetworkError) {
        return;
      }

      reportToBugsnag(new ExtendedError('Failed to perform bulk update conversations.', error));
    }
  };
}

/*
 * Helpers.
 */

function serializeConversationSelection(
  parameters: InboxesConversationsParameters,
  cellsContext?: CellsContext,
) {
  if (!computeIsAllExceptSelection(parameters.selection)) {
    throw new Error('Bulk actions not supported in normal conversation selection.');
  }

  // Search queries require a different path.
  if (parameters.search) {
    return {
      exclude: parameters.selection.deselectedIds,
      search: {
        search_query: buildFullSearchQuery(parameters, parameters.search, cellsContext),
        query_options: {
          context: buildParametersFetchQueryContext(parameters, cellsContext),
          filters: parameters.filters,
        },
      },
    };
  }

  return {
    exclude: parameters.selection.deselectedIds,
    ...serializeConversationParameters(parameters),
  };
}

function serializeConversationParameters(parameters: InboxesConversationsParameters) {
  switch (parameters.type) {
    case InboxesParametersTypesEnum.INBOX:
    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return {
        inbox: {
          inbox_id: parameters.inboxId,
          filters: parameters.filters,
        },
      };

    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return {
        tag: {
          tag_id: parameters.viewId,
          filters: parameters.filters,
        },
      };

    case InboxesParametersTypesEnum.USER:
    case InboxesParametersTypesEnum.TEAMMATE:
      return {
        teammate: {
          teammate_id: parameters.teammateId,
          folder: parameters.folder,
          filters: parameters.filters,
        },
      };

    case InboxesParametersTypesEnum.TEAM:
      return {
        team: {
          team_id: parameters.teamId,
          filters: parameters.filters,
        },
      };

    case InboxesParametersTypesEnum.GLOBAL:
      return {
        unified: {
          folder: parameters.folder,
          filters: parameters.filters,
        },
      };

    default:
      throw new Error('Unexpected inbox parameter type');
  }
}
